import { PencilSimpleIcon, PlusIcon } from '@phosphor-icons/react';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';

import { Button } from '@/components/common/button/button';
import { DatePicker } from '@/components/common/date-picker';
import { useToast } from '@/components/feedback/toast';
import type { OrderDetail } from '@/features/orders';
import {
  formatDeliveryDate,
  isFinalStatus,
  isPrePlacementStatus,
  useUpdateExpectedDelivery,
} from '@/features/orders';
import { appDayOf, fromDate, toDayjs } from '@/lib/date';

import styles from './expected-delivery-field.module.scss';

export function ExpectedDeliveryField({ order }: { order: OrderDetail }) {
  const [editing, setEditing] = useState(false);
  const toast = useToast();
  const save = useUpdateExpectedDelivery(order.id);
  const { status, orderMeta, deliveredAt } = order;

  // Rendered but unusable before payment (122372 §3f; Figma 2704:568933) — not hidden, and not the
  // read-only text a final order shows. The server would 409 the write anyway.
  if (isPrePlacementStatus(status)) {
    return (
      <Button variant="outline" disabled>
        <PlusIcon size={12} aria-hidden /> Add
      </Button>
    );
  }

  if (isFinalStatus(status)) {
    const value = status === 'delivered' ? deliveredAt : orderMeta.expectedDelivery;
    return <span className={styles.readonly}>{formatDeliveryDate(value)}</span>;
  }

  if (editing) {
    return (
      <DatePicker
        open
        autoFocus
        value={toDayjs(orderMeta.expectedDelivery) ?? null}
        // The order's Eastern day — the day the server's before-order-date check uses (ADO 120986).
        disabledDate={(current) =>
          !!orderMeta.orderDate && current.isBefore(appDayOf(orderMeta.orderDate), 'day')
        }
        onChange={(value) => {
          const next = fromDate(value as Dayjs | null);
          if (!next) return;
          save.mutate(next, {
            onSuccess: () => toast.success({ title: 'Expected delivery date updated.' }),
            onError: () =>
              toast.error({ title: 'Could not update the delivery date. Please try again.' }),
            onSettled: () => setEditing(false),
          });
        }}
        onOpenChange={(open) => {
          if (!open) setEditing(false);
        }}
      />
    );
  }

  if (!orderMeta.expectedDelivery) {
    return (
      <Button variant="outline" onClick={() => setEditing(true)}>
        <PlusIcon size={12} aria-hidden /> Add
      </Button>
    );
  }

  return (
    <span className={styles.filled}>
      {formatDeliveryDate(orderMeta.expectedDelivery)}
      <button
        type="button"
        className={styles.edit}
        aria-label="Edit expected delivery"
        onClick={() => setEditing(true)}
      >
        <PencilSimpleIcon size={16} aria-hidden />
      </button>
    </span>
  );
}
